<template>
  <div class="living-options-masterplan">
    <div class="row">
      <div class="col-12 mb-4">
        <language-selector />
        <div class="living-options" id="living-options">
          <h3>Living Options</h3>
          <button class="btn btn-sm btn-primary mb-4" @click="addLivingOption">
            Add Living Option
          </button>

          <div class="row mb-4">
            <div class="col-6">
              <div class="status-option">
                <input
                  type="checkbox"
                  id="active"
                  value="Active"
                  v-model="filterActiveStates"
                />
                <label class="status-option__label" for="active">Active</label>
              </div>

              <div class="status-option">
                <input
                  type="checkbox"
                  id="inactive"
                  value="Inactive"
                  v-model="filterActiveStates"
                />
                <label class="status-option__label" for="inactive"
                  >Inactive</label
                >
              </div>
            </div>
          </div>

          <div class="mb-2">
            <input
              class="form-control"
              type="text"
              v-model="livingOptionsFilterText"
              placeholder="Filter Living Options"
            />
          </div>

          <v-table
            :columns="[
              { label: 'Label', field: 'label' },
              {
                label: 'Sold out',
                field: 'isSoldOut',
                options: {
                  sortable: true,
                  return: function(fieldValue, row) {
                    if (fieldValue) {
                      return `<div class='status status--sold'>Manually Set</div>`;
                    } else if (row.availablePropertiesCount === 0) {
                      return `<div class='status status--sold'>Available properties: ${row.availablePropertiesCount}</div>`;
                    } else {
                      return `<div class='status status--available'>Available properties: ${row.availablePropertiesCount}</div>`;
                    }
                  }
                }
              },
              {
                label: 'Associated Releases',
                field: 'subsectionIds',
                sortField: 'subsectionIds.length',
                options: {
                  sortable: true,
                  return: function(fieldValue) {
                    if (fieldValue.length) {
                      return fieldValue.length;
                    } else {
                      return '-';
                    }
                  }
                }
              }
            ]"
            :rows="filteredLivingOptions"
            :show-active-toggle="
              activeUser.access.indexOf('live-toggle') !== -1
            "
            save-action="SAVE_LIVING_OPTION"
            @edit="editLivingOption($event._id)"
            @delete="deleteLivingOption($event._id)"
          />
        </div>
      </div>

      <div class="col-12 mb-4">
        <div class="form-group">
          <label>
            Masterplan Background Media
            <localised-field-indicator />
          </label>
          <input-asset
            v-model="liveHereBackground[activeLanguage.code]"
          ></input-asset>
        </div>
        <div class="form-group">
          <label>Background Video Settings</label>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="liveHereBackgroundVideoSettings.loop"
            />
            <span class="ml-2">Loop Video</span>
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="liveHereBackgroundVideoSettings.showControls"
            />
            <span class="ml-2">
              Show Controls
              <InfoBox>Show the video player controls (pause, mute).</InfoBox>
            </span>
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="liveHereBackgroundVideoSettings.delayTags"
            />
            <span class="ml-2">
              Delay Tags
              <InfoBox>
                Delay animating tags in until after video play back has
                finished.
              </InfoBox>
            </span>
          </div>
          <div class="d-flex">
            <input
              type="checkbox"
              v-model="liveHereBackgroundVideoSettings.startMuted"
            />
            <span class="ml-2">
              Start Muted
              <InfoBox>
                Start the video with the sound muted. Can be unmuted if "Show
                Controls" is checked.
              </InfoBox>
            </span>
          </div>
        </div>
        <div class="form-group">
          <label>Masterplan Background Image Fallback</label>
          <input-asset v-model="liveHereBackgroundFallback"></input-asset>
        </div>
        <div class="form-group">
          <label>Masterplan Access PIN</label>
          <input
            v-model="liveHereMasterplanAccessCode"
            type="text"
            class="form-control"
            autocomplete="off"
          />
        </div>
        <div v-if="liveOptionConfigTags" class="card-update-tags">
          <card>
            <template #header>
              <div class="title-row">
                <h4 class="card-title mb-0">Living Option Placement</h4>

                <div class="info-icon-wrapper">
                  <div class="edit-tags-info">
                    <InfoBox>
                      <div class="edit-tags-info__section">
                        <div class="edit-tags-info__label">Basics</div>
                        <ul>
                          <li>
                            Click <strong>Add Tag</strong> to create a new tag.
                          </li>
                          <li>
                            Drag tags to reposition them on the masterplan.
                          </li>
                          <li>
                            Click a tag to select it;
                            <strong>double-click</strong> to open the Edit Menu
                            (pen icon) or click the icon itself.
                          </li>
                        </ul>
                      </div>

                      <div class="edit-tags-info__section">
                        <div class="edit-tags-info__label">Toolbar</div>
                        <ul>
                          <li>
                            Use the left toolbar for <strong>Zoom</strong>,
                            <strong>Undo / Redo</strong> and
                            <strong>Alignment</strong> tools.
                          </li>
                          <li>
                            Selected tags can be aligned or moved together.
                          </li>
                        </ul>
                      </div>

                      <div class="edit-tags-info__section">
                        <div class="edit-tags-info__label">Shortcuts</div>
                        <ul>
                          <li>
                            Hold <strong>Shift</strong> to multi-select tags.
                          </li>
                          <li>
                            Hold <strong>Shift</strong> when editing polygons to
                            toggle '+' icon used for adding vertices.
                          </li>
                          <li>
                            <strong>Double-Click</strong> a vertex/point on a
                            polygon to remove it.
                          </li>
                          <li>
                            <strong>Shift + Double-Click</strong> a tag to
                            select all tags of that type.
                          </li>
                          <li>
                            Use <strong>Shift +</strong> to zoom in and
                            <strong>Shift -</strong> to zoom out.
                            <strong>Shift 0</strong> to reset to default view.
                          </li>
                          <li>
                            Press <strong>Delete</strong> or
                            <strong>Backspace</strong> to remove a selected
                            tag/element.
                          </li>
                          <li>
                            Press <strong>Esc</strong> to clear selection or
                            exit edit mode.
                          </li>
                          <li>
                            Use <strong>Arrow Keys</strong> to adjust position
                            for 'Pixel Perfect' placement.
                          </li>
                        </ul>
                      </div>
                    </InfoBox>
                  </div>
                </div>
              </div>
            </template>

            <button class="btn btn-primary my-2 mr-2" @click="addMasterplanTag">
              Add Tag
            </button>

            <draggable-elements
              :tags="liveOptionConfigTags.value"
              :living-option-ids="livingOptionIds"
              :background-image="liveHereBackground[activeLanguage.code]"
            />
          </card>
        </div>

        <div class="form-group" id="sidebar-heading">
          <label>
            Sidebar Heading
            <localised-field-indicator />
          </label>
          <input
            v-model="liveHereHeading[activeLanguage.code]"
            id="liveHeading"
            type="text"
            class="form-control"
          />
        </div>

        <div class="form-group" id="sidebar-description">
          <label>
            Sidebar Description
            <localised-field-indicator />
          </label>
          <ck-editor
            v-model="liveHereDescription[activeLanguage.code]"
            id="liveDescription"
          ></ck-editor>
        </div>
        <div class="form-group">
          <label>Living Sidebar Minimap</label>
          <input-asset v-model="liveHereSidebarMinimap"></input-asset>
        </div>
        <button class="btn btn-sm btn-primary" @click="saveLivingOptions">
          Save
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import { FETCH_CONFIG_ITEMS, CONFIG_ITEMS_EDIT } from "@/store/actions.type";
import CkEditor from "@/components/CKEditor";
import DotDropper from "@/components/DotDropper";
import InputAsset from "@/components/InputAsset";
import VTable from "@/components/VTable";
import InfoBox from "@/components/InfoBox";
import DraggableElements from "@/components/DraggableElements";

export default {
  components: {
    CkEditor,
    DotDropper,
    InputAsset,
    VTable,
    InfoBox,
    DraggableElements
  },

  data() {
    return {
      configItems: [],
      liveHereSidebarMinimap: "",
      liveHereDescription: {
        en: ""
      },
      liveHereHeading: {
        en: ""
      },
      liveHereBackground: {
        en: ""
      },
      liveHereBackgroundVideoSettings: {
        loop: false,
        showControls: false,
        delayTags: false,
        startMuted: false
      },
      liveHereBackgroundFallback: "",
      liveHereMasterplanAccessCode: "",
      livingOptionsConfigItemsKeys: [
        "liveHereSidebarMinimap",
        "liveHereHeading",
        "liveHereDescription",
        "liveHereBackground",
        "liveHereBackgroundFallback",
        "liveHereBackgroundVideoSettings",
        "liveHereMasterplanAccessCode"
      ],

      activeLivingOptionId: null,
      livingOptionsFilterText: "",
      filterActiveStates: []
    };
  },

  computed: {
    ...mapGetters(["activeLanguage", "livingOptions"]),

    activeUser() {
      return this.$store.getters.user;
    },

    livingOptionIds() {
      return this.livingOptions.map(livingOption => livingOption._id);
    },

    liveOptionConfigTags() {
      return this.configItems.find(
        item => item.key === "liveHereMasterplanTags"
      );
    },

    filteredLivingOptions() {
      let filtered = this.livingOptions.filter(item =>
        item.label
          .toLowerCase()
          .includes(this.livingOptionsFilterText.toLowerCase())
      );

      if (this.filterActiveStates.length) {
        filtered = filtered.filter(
          item =>
            (this.filterActiveStates.includes("Active") && item.active) ||
            (this.filterActiveStates.includes("Inactive") && !item.active)
        );
      }

      return filtered;
    }
  },

  created() {
    this.$store.dispatch("FETCH_ASSETS");
    this.$store.dispatch("FETCH_LIVING_OPTIONS");
    this.setData();
  },

  methods: {
    async setData() {
      this.configItems = await this.$store.dispatch(FETCH_CONFIG_ITEMS);

      const liveConfigItems = this.configItems.filter(item =>
        this.livingOptionsConfigItemsKeys.includes(item.key)
      );

      // Map the live items to the component data
      liveConfigItems.forEach(item => {
        this[item.key] = item.value;
      });
    },

    async addLivingOption() {
      const newLivingOption = await this.$store.dispatch(
        "ADD_NEW_LIVING_OPTION"
      );
      this.$store.dispatch("SAVE_LIVING_OPTION", newLivingOption);
      this.editLivingOption(newLivingOption._id);
    },

    editLivingOption(id) {
      this.$router.push(`masterplan/${id}`);
    },

    deleteLivingOption(id) {
      if (confirm("Are you sure?")) {
        this.$store.dispatch("DELETE_LIVING_OPTION", id);
      }
    },

    saveConfig() {
      this.configItems.forEach(configItem => {
        this.livingOptionsConfigItemsKeys.forEach(key => {
          if (key === configItem.key) {
            configItem.value = this[key];
          }
        });
      });
      this.$store.dispatch(CONFIG_ITEMS_EDIT, this.configItems);
    },

    saveLivingOptions() {
      this.saveConfig();
      this.livingOptions.forEach(livingOption => {
        if (livingOption.active) {
          this.$store.dispatch("SAVE_LIVING_OPTION", livingOption);
        }
      });
    },

    addMasterplanTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then(res => {
        this.liveOptionConfigTags.value.push(res.data);
      });
    },

    addLivingOptionTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then(res => {
        this.activeLivingOption.tags.push(res.data);
      });
    },

    backgroundAssetType(asset) {
      if (asset) {
        if (asset.split(".").pop() === "mp4") {
          return "video";
        } else {
          return "image";
        }
      }

      return "";
    }
  }
};
</script>

<style lang="scss" scoped>
.card-update-tags {
  position: relative;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  transform: translateY(3px);
  z-index: 5000;
}

.edit-tags-info {
  max-width: 380px;

  &__section {
    &:not(:last-child) {
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    ul {
      margin: 4px 0 0;
      padding-left: 18px;
      font-size: 13px;
      line-height: 1.45;
    }
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7a7a85;
    font-weight: 600;
  }
}
</style>
